import React from 'react';
import {observer} from 'mobx-react';
import {FormattedMessage} from 'react-intl';
import {rangeDate} from 'shared/tools';
import {InteractiveAccordionTable} from 'main/components';
import {endpoints, types, auth} from 'main/tools';
import Document from 'main/stores/documents/Document';
import Folder from 'main/stores/folders/Folder';
import _ from 'lodash';

const SharedStatus = ({model}) => {
  if (model.shareWithEmployee) {
    return <FormattedMessage id='employees.profile.documents.shared_status.Yes'/>;
  }

  return <FormattedMessage id='employees.profile.documents.shared_status.No'/>;
};

const Name = ({model, level}) => {
  const fileUrl = _.get(model, 'links.fileUrl');
  let nameElement;
  if (!fileUrl) {
    nameElement = <span>{model.fileName}</span>;
  } else {
    nameElement = <a href={fileUrl} target='_blank' className='dodger'>{model.fileName}</a>;
  }

  return (
    <div>
      {!!model.folderName && level === 0 && <div className='flex align-items-middle mb1'>
        <i className='jumbo mr1 material-icons h4'>arrow_forward</i>
        <span className='jumbo'>{model.folderName}</span>
      </div>}
      {nameElement}
    </div>
  );
};

const CreatedAtDate = ({model}) => {
  if (!model.createdAt) return null;

  return <span>{rangeDate(model.createdAt)}</span>;
};

const COLUMNS_WITHOUT_SHARED = [
  {
    header: 'employees.profile.documents.Name',
    component: Name,
    sortBy: 'fileName',
    className: 'breakword',
    width: 8
  },
  {
    header: 'employees.profile.documents.Date Added',
    component: CreatedAtDate,
    sortBy: 'createdAt',
    width: 3
  }
];

const COLUMNS_WITH_SHARED = [
  {
    header: 'employees.profile.documents.Name',
    component: Name,
    sortBy: 'fileName',
    className: 'breakword',
    width: 6
  },
  {
    header: 'employees.profile.documents.Date Added',
    component: CreatedAtDate,
    sortBy: 'createdAt',
    width: 3
  },
  {
    header: 'employees.profile.documents.Shared with employee',
    component: SharedStatus,
    width: 2
  }
];

const FOLDER_COLUMNS = [
  {
    header: 'employees.profile.documents.Name',
    attribute: 'name',
    width: 11
  }
];

const NESTED_ROWS = [
  {attribute: 'documents'}
];

const LOCALIZATION = {
  removeModal: {
    header: 'employees.profile.documents.Delete Document',
    subHeader: 'employees.profile.documents.Are you sure you want to delete this document?',
    saveCaption: 'employees.profile.documents.Delete'
  },
  emptyState: 'company_settings.documents.No documents to display',
  interactive: {
    addModel: 'employees.profile.documents.Add Document'
  },
  removeModel: 'employees.profile.documents.Delete'
};

const EmployeeDocumentList = observer(({uiState}) => {
  const columns = uiState.canShareWithEmployee ? COLUMNS_WITH_SHARED : COLUMNS_WITHOUT_SHARED;

  return (
    <InteractiveAccordionTable
      columns={columns}
      folderColumns={FOLDER_COLUMNS}
      nestedRows={NESTED_ROWS}
      localization={LOCALIZATION}
      canExpand={item => item.hasDocuments || item.hasSubfolders}
      onExpand={(folder, filter) => uiState.fetchFolderDocuments(folder, filter)}
      customLinks={model => uiState.documentCustomLinks(model)}
      customFolderLinks={uiState.customFolderLinks}
      onEditClicked={model => uiState.openEditDocumentModal(model)}
      onRemove={model => uiState.deleteDocument(model)}
      onMount={agent => uiState.setInteractiveAgent(agent)}
      showAddButton={false}
      wrapInPanel={false}
      proxy={{
        type: 'async',
        modelType: types.DOCUMENT,
        model: Document,
        endpoint: endpoints.EMPLOYEE_DOCUMENTS.with(uiState.employeeId)
      }}
      folderProxy={{
        endpoint: endpoints.FOLDERS.FOR_EMPLOYEE.with(uiState.employeeId, null),
        model: Folder,
        modelType: types.FOLDER
      }}
    />
  );
});

export default EmployeeDocumentList;
